import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import Blocks from '../blocks/Blocks';
import FilmCard from '../cards/Film';
import VenueCard from '../cards/Venue';
import Date from '../partials/Date';
import Description from '../partials/Description';
import FilmTrailer from '../partials/FilmTrailer';
import Image from '../partials/Image';
import PageMeta from '../partials/PageMeta';
import Title from '../typography/Title';

import styles from '../../../styles/pages/event.module.css';

const propTypes = { page: PropTypes.shape({ id: PropTypes.string }) };

function EventPage({ page = null }) {
    const {
        blocks = null,
        title = null,
        layout = null,
        films = null,
        description = null,
        guestNote = null,
        startDate = null,
        venues = null,
        inDocWeek = false,
        image = null,
    } = page || {};

    const featureFilms = (films || []).filter(({ type = null }) => type === 'feature');
    const shortFilms = (films || []).filter(({ type = null }) => type === 'short');
    const [firstFilm = null] = featureFilms || shortFilms || [];
    const [firstVenue = null] = venues || [];
    const { name: venueName = null } = firstVenue || {};

    return (
        <div className={styles.container}>
            <PageMeta page={page} />
            <div className={styles.top}>
                {startDate !== null ? (
                    <Title className={styles.date}>
                        <Date date={startDate} />
                    </Title>
                ) : null}
                {title !== null ? (
                    <Title className={styles.title} size="large">
                        {title}
                    </Title>
                ) : null}
                {firstVenue !== null ? (
                    <Title className={styles.description}>{firstVenue?.nameWithCity}</Title>
                ) : null}
            </div>

            {firstFilm !== null ? (
                <FilmTrailer
                    className={styles.image}
                    film={firstFilm}
                    inDocWeek={inDocWeek}
                    withoutFavorites
                />
            ) : (
                <Image
                    className={styles.image}
                    image={image}
                    withDocWeek={inDocWeek}
                    withDocLink={inDocWeek}
                    size="large"
                    filtered
                    rounded
                    header
                />
            )}
            <div className={styles.content}>
                {description !== null ? (
                    <Description className={styles.description} body={description} />
                ) : null}
                {guestNote !== null ? (
                    <Description className={styles.description} body={guestNote} />
                ) : null}
                <p className={styles.venuesLabel}>
                    <FormattedMessage defaultMessage="Presented at :" description="Event label" />
                </p>
                {venues !== null && venues.length > 0
                    ? venues.map((venue) => <VenueCard key={`venue-${venue?.id}`} item={venue} />)
                    : null}
            </div>
            <Title className={styles.title} subtitle>
                <FormattedMessage defaultMessage="Films presented :" description="Section title" />
            </Title>
            <div className={styles.films}>
                {featureFilms.length > 0 ? (
                    <div className={styles.section}>
                        <Title className={styles.sectionTitle} size="small" subtitle>
                            <FormattedMessage
                                defaultMessage="Feature films"
                                description="Section title"
                            />
                        </Title>
                        {(featureFilms || []).map((film) => (
                            <FilmCard key={`film-${film.id}`} item={film} wide />
                        ))}
                    </div>
                ) : null}
                {shortFilms.length > 0 ? (
                    <div className={styles.section}>
                        <Title className={styles.sectionTitle} size="small" subtitle>
                            <FormattedMessage
                                defaultMessage="Short films"
                                description="Section title"
                            />
                        </Title>
                        {(shortFilms || []).map((film) => (
                            <FilmCard key={`film-${film.id}`} item={film} wide />
                        ))}
                    </div>
                ) : null}
            </div>
            {blocks !== null && blocks.length > 0 ? (
                <Blocks
                    className={styles.blocks}
                    blocks={blocks}
                    withoutLines={layout === 'week'}
                />
            ) : null}
        </div>
    );
}

EventPage.propTypes = propTypes;

export default EventPage;
